import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PlusCircle, LogOut, Edit3, Trash2, EyeOff, Eye, Archive, ArchiveRestore, ArrowUp, ArrowDown,
  Send, Loader2, Copy, Link2, Check, ChevronsUp, ChevronDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  adminListPosts, deletePost, updatePost, formatApiErrorDetail, api,
} from "../lib/api";
import { usePostTypes } from "../lib/postTypes";
import AdminHealthBadge from "../components/AdminHealthBadge";
import AdminHomePostCountPanel from "../components/AdminHomePostCountPanel";
import AdminGlobalSearch from "../components/AdminGlobalSearch";

const BUILTIN_LABELS = { long: "Long post", short: "Short note", note: "Note" };
const POSTS_PAGE_SIZE = 15;

// Everything except the always-visible #PUSB shortcut above, grouped per
// the admin's requested layout. Each item keeps the exact same `to` +
// `testid` it always had — only the grouping/presentation changed, no
// routes moved.
// Order matches the admin's requested category-button row order:
// Admin, Media, Interactive, Design, Content, Shop.
const NAV_GROUPS = [
  {
    id: "admin",
    label: "Admin",
    items: [
      { to: "/admin/analytics",    label: "Analytics",         testid: "admin-link-analytics" },
      { to: "/admin/analytics-v2", label: "Analytics (Beta)",  testid: "admin-link-analytics-v2" },
      { to: "/admin/backup",       label: "Backup / Restore",  testid: "admin-link-backup" },
      { to: "/admin/todos",        label: "To-do list",        testid: "admin-link-todos" },
    ],
  },
  {
    id: "media",
    label: "Media",
    items: [
      { to: "/admin/media",      label: "Media library",   testid: "admin-link-media" },
      { to: "/admin/carousels",  label: "Photo carousels",  testid: "admin-link-carousels" },
    ],
  },
  {
    id: "interactive",
    label: "Interactive",
    items: [
      { to: "/admin/polls",                  label: "Fan Voting Polls",             testid: "admin-link-polls" },
      { to: "/admin/forum",                  label: "Forum (board)",                testid: "admin-link-forum" },
      { to: "/admin/comments",               label: "Comments",                     testid: "admin-link-comments" },
      { to: "/admin/homepage-widgets",       label: "Homepage widgets",             testid: "admin-link-homepage-widgets" },
      { to: "/admin/inbox",                  label: "Inbox",                        testid: "admin-link-inbox" },
      { to: "/admin/classic-lineup-presets", label: "Classic Lineup Presets",       testid: "admin-link-classic-lineup-presets" },
      { to: "/admin/classic-shirts",         label: "Classic Shirt Designs",        testid: "admin-link-classic-shirts" },
      { to: "/admin/classic-squad",          label: "Classic Squad List (Lineups)", testid: "admin-link-classic-squad" },
      { to: "/admin/squad",                  label: "Squad List (Lineups)",         testid: "admin-link-squad" },
    ],
  },
  {
    id: "design",
    label: "Design",
    items: [
      { to: "/admin/explore-rotation", label: "Home Explore rotation",       testid: "admin-link-explore-rotation" },
      { to: "/admin/homepage-strip",   label: "Homepage strip",              testid: "admin-link-homepage-strip" },
      { to: "/admin/page-tiles",       label: "Tiles on any page",           testid: "admin-link-page-tiles" },
      { to: "/admin/theme",            label: "Theme",                       testid: "admin-link-theme" },
      { to: "/admin/nav",              label: "Nav menu (header + mobile)",  testid: "admin-link-nav" },
      { to: "/admin/footer-links",     label: "Footer links",                testid: "admin-link-footer-links" },
    ],
  },
  {
    id: "content",
    label: "Content",
    items: [
      { to: "/admin/post-types",     label: "Post types",         testid: "admin-link-post-types" },
      { to: "/admin/post-templates", label: "Post templates",     testid: "admin-link-post-templates" },
      { to: "/admin/page-content",   label: "Page headings",      testid: "admin-link-page-content" },
      { to: "/admin/news-sources",   label: "News sources",       testid: "admin-link-news-sources" },
      { to: "/admin/custom-pages",   label: "Custom pages",       testid: "admin-link-custom-pages" },
      { to: "/admin/video-channels", label: "YouTube channels",   testid: "admin-link-video-channels" },
      { to: "/admin/spotify",        label: "Spotify playlist",   testid: "admin-link-spotify" },
    ],
  },
  {
    id: "shop",
    label: "Shop",
    items: [
      { to: "/admin/products",           label: "Products",            testid: "admin-link-products" },
      { to: "/admin/product-categories", label: "Product categories",  testid: "admin-link-product-categories" },
      { to: "/admin/ink-shop",           label: "Ink shop (POD)",      testid: "admin-link-ink-shop" },
      { to: "/admin/ink-orders",         label: "Ink shop orders",     testid: "admin-link-ink-orders" },
    ],
  },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { types: postTypes, label: postTypeLabel } = usePostTypes();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pushBusy, setPushBusy] = useState(null);
  const [pushFlash, setPushFlash] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);

  // Builtin types (long/short/note) aren't in the admin-managed
  // /post-types list, so stitch them into the filter dropdown alongside
  // whatever custom types the admin has created (transfer in/out, on
  // this day, etc.) — same source the row badges already read from.
  const filterOptions = [
    ...Object.entries(BUILTIN_LABELS).map(([id, label]) => ({ id, label })),
    ...postTypes,
  ];
  const filteredPosts = typeFilter
    ? posts.filter((p) => (p.post_type || "long") === typeFilter)
    : posts;

  // Reset to page 1 whenever the type filter changes, or the underlying
  // list changes shape (e.g. a delete drops the current page below the
  // new total) — never leave the admin stranded on a page with nothing
  // on it.
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PAGE_SIZE));
  useEffect(() => {
    setPage(1);
  }, [typeFilter]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);
  const pageStart = (page - 1) * POSTS_PAGE_SIZE;
  // Move up/down/top rely on the ABSOLUTE index within the full `posts`
  // array (that's what the backend's ordered `position` field reflects),
  // so pagination only controls which rows are rendered — it must never
  // change what `idx` means below. Only the ids on the current page get
  // rendered; everything else still exists in `posts` for numbering +
  // move-boundary checks.
  const visiblePostIds = new Set(
    filteredPosts.slice(pageStart, pageStart + POSTS_PAGE_SIZE).map((p) => p.id),
  );

  const getDisplayNumber = (post, index) => {
    if (!post.published || post.archived) return "—";
    // Count how many published, unarchived posts exist *before* or at this index
    let count = 0;
    for (let i = 0; i <= index; i++) {
      const p = posts[i];
      if (p && p.published && !p.archived) {
        count++;
      }
    }
    return `#${count}`;
  };

  const getPositionBadgeStyle = (post) => {
    if (post.published && !post.archived) {
      return "bg-[#6CABDD]/10 text-[#002d75]";
    }
    return "bg-[#0A0F17]/5 text-[var(--brand-body)]";
  };

  const load = async () => {
    setLoading(true);
    try {
      const p = await adminListPosts();
      setPosts(p);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const togglePublish = async (post) => {
    try {
      await updatePost(post.id, { published: !post.published });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleArchive = async (post) => {
    try {
      await updatePost(post.id, { archived: !post.archived });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await deletePost(post.id);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const movePost = async (post, direction) => {
    setError("");
    try {
      // Use the same PUT /posts/{id} endpoint Archive/Unpublish use, by
      // sending `move_direction` instead of the dedicated /move POST. The
      // dedicated POST appears to be silently dropped by something on the
      // production stack (no HTTP status reaches the browser).
      await updatePost(post.id, { move_direction: direction });
      await load();
    } catch (e) {
      const status = e.response?.status;
      if (!e.response) {
        setError(`Move failed (no response): ${e.message || "network error"}`);
        return;
      }
      const detail = formatApiErrorDetail(e.response.data?.detail) || e.message;
      setError(`Move failed (HTTP ${status}): ${detail}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const duplicatePost = async (p) => {
    setError("");
    try {
      const r = await api.post(`/admin/posts/${p.id}/duplicate`);
      await load();
      // Open the freshly duplicated draft in the editor so the admin
      // lands ready to tweak the copy rather than scrolling for it.
      if (r.data?.id) navigate(`/admin/posts/${r.data.id}/edit`);
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(status ? `Duplicate failed (HTTP ${status}): ${detail}` : `Duplicate failed: ${detail}`);
    }
  };

  // Copy this post's public URL to the clipboard with a 1.5s "Copied"
  // confirmation so the admin doesn't have to open the editor to grab
  // the share link.
  const [copiedPostId, setCopiedPostId] = useState("");
  const copyPostUrl = async (p) => {
    const slug = p.slug || p.id;
    const url = `${window.location.origin}/post/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for browsers without clipboard API access (e.g. http)
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* nothing more to try */ }
      document.body.removeChild(ta);
    }
    setCopiedPostId(p.id);
    setTimeout(() => setCopiedPostId((cur) => (cur === p.id ? "" : cur)), 1500);
  };

  const sendPush = async (p) => {
    if (pushBusy) return;
    const confirmed = window.confirm(
      `Push "${p.title}" to subscribers now? This sends a notification to every device that opted in.`,
    );
    if (!confirmed) return;
    setPushBusy(p.id);
    setPushFlash("");
    setError("");
    try {
      // Prefer the admin's custom intro (matches what readers see on the
      // home/archive cards) over the auto excerpt from the body.
      const source = (p.custom_intro || p.body || "");
      const excerpt = source
        .replace(/<[^>]+>/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 160);
      const r = await api.post("/admin/push/send", {
        title: p.title,
        body: excerpt,
        url: `/post/${p.slug || p.id}`,
      });
      const { success = 0, expired = 0, failed = 0 } = r.data || {};
      setPushFlash(
        `Push sent — delivered ${success}${expired ? `, ${expired} expired removed` : ""}${failed ? `, ${failed} failed` : ""}.`,
      );
      window.setTimeout(() => setPushFlash(""), 5000);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPushBusy(null);
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]"
      data-testid="page-admin"
    >
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex flex-col leading-tight">
              <span className="font-display text-base font-black tracking-tight">
                LAST
              </span>
              <span className="label-kicker text-[var(--brand-body)]">
                Admin · {user?.name || user?.email}
              </span>
            </div>
          </Link>
          <AdminGlobalSearch />
          <div className="flex items-center gap-2">
            <AdminHealthBadge />
            <Link
              to="/"
              className="rounded-md border border-black/10 px-3 py-2 font-display text-sm font-semibold hover:border-[#0A0F17]"
            >
              View site
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-2 font-display text-sm font-semibold text-white"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <div className="mb-8 flex flex-wrap items-center gap-2">
          <Link
            to="/admin/posts/new"
            data-testid="admin-new-post"
            className="inline-flex items-center gap-2 rounded-md bg-[#6CABDD] px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-white hover:bg-[#5A9AC9]"
          >
            <PlusCircle size={16} /> New post
          </Link>
          <Link
            to="/admin/social"
            data-testid="admin-link-social"
            className="inline-flex items-center gap-2 rounded-md border-2 border-[#0A0F17] bg-[#0A0F17] px-4 py-2.5 font-display text-sm font-bold text-white hover:opacity-90"
          >
            #PUSB social wall →
          </Link>
        </div>

        <AdminNavCategories />

        {/* How many posts show on the home page, desktop vs mobile — see
            Home.jsx's matchMedia check for how these two values are used. */}
        <AdminHomePostCountPanel />

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]"
            data-testid="admin-error"
          >
            {error}
          </p>
        ) : null}
        {pushFlash ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#6CABDD] bg-[#6CABDD]/10 px-3 py-2 text-sm text-[var(--brand-ink)]"
            data-testid="admin-push-flash"
          >
            {pushFlash}
          </p>
        ) : null}

        {!loading && posts.length > 0 ? (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <label htmlFor="admin-post-type-filter" className="label-kicker text-[var(--brand-body)]">
              Filter by type
            </label>
            <select
              id="admin-post-type-filter"
              data-testid="admin-post-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-md border border-black/10 bg-white px-3 py-2 text-sm font-semibold"
            >
              <option value="">All types</option>
              {filterOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
            {typeFilter ? (
              <span className="label-kicker text-[var(--brand-body)]" data-testid="admin-post-filter-count">
                Showing {filteredPosts.length} of {posts.length}
              </span>
            ) : null}
          </div>
        ) : null}

        {loading ? (
          <div className="space-y-3" data-testid="admin-loading">
            {["s1","s2","s3"].map((id) => (
              <div
                key={id}
                className="h-20 animate-pulse rounded-md border border-black/10 bg-white"
              />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 bg-white p-10 text-center"
            data-testid="admin-empty"
          >
            <p className="font-display text-lg font-bold">
              No posts yet.
            </p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Create your first update — it takes about 20 seconds.
            </p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 bg-white p-10 text-center"
            data-testid="admin-empty-filtered"
          >
            <p className="font-display text-lg font-bold">
              No posts of this type.
            </p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Try a different type, or clear the filter to see everything.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="admin-post-list">
            {posts.map((p, idx) => {
              if (typeFilter && (p.post_type || "long") !== typeFilter) return null;
              if (!visiblePostIds.has(p.id)) return null;
              return (
              <li
                key={p.id}
                data-testid="admin-post-row"
                className="flex flex-col gap-3 rounded-md border border-black/10 bg-white p-5 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <span 
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-display text-sm font-bold ${getPositionBadgeStyle(p)}`}
                    data-testid={`admin-post-position-${idx}`}
                  >
                    {getDisplayNumber(p, idx)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded-sm px-2 py-1 label-kicker ${
                          p.published
                            ? "bg-[#6CABDD] text-white"
                            : "bg-[#E2E8F0] text-[var(--brand-body)]"
                        }`}
                      >
                        {p.published ? "Published" : "Draft"}
                      </span>
                      {p.archived ? (
                        <span className="rounded-sm bg-[#475569] px-2 py-1 label-kicker text-white">
                          Archived
                        </span>
                      ) : null}
                      {p.publish_at && p.publish_at > new Date().toISOString() ? (
                        <span
                          className="rounded-sm bg-[#F59E0B] px-2 py-1 label-kicker text-white"
                          title={`Scheduled for ${new Date(p.publish_at).toLocaleString("en-GB")}`}
                          data-testid="admin-post-scheduled-badge"
                        >
                          Scheduled · {new Date(p.publish_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                        </span>
                      ) : null}
                      <span className="label-kicker text-[var(--brand-body)]">
                        {BUILTIN_LABELS[p.post_type] || postTypeLabel(p.post_type)}
                      </span>
                    </div>
                    <h3 className="mt-2 font-display text-lg font-bold break-words">
                      {p.title}
                    </h3>
                    <p className="label-kicker text-[var(--brand-body)]">
                      {new Date(p.created_at).toLocaleString("en-GB")}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div
                    className="mr-1 flex items-center overflow-hidden rounded-md border border-black/10"
                    data-testid={`admin-post-reorder-${p.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => movePost(p, "top")}
                      disabled={idx === 0}
                      data-testid="admin-post-move-top"
                      title="Send to top"
                      className="px-2 py-2 text-[var(--brand-body)] hover:bg-black/5 hover:text-[var(--brand-ink)] disabled:opacity-30 disabled:cursor-not-allowed border-r border-black/10"
                    >
                      <ChevronsUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => movePost(p, "up")}
                      disabled={idx === 0}
                      data-testid="admin-post-move-up"
                      title="Move up"
                      className="px-2 py-2 text-[var(--brand-body)] hover:bg-black/5 hover:text-[var(--brand-ink)] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <span className="h-5 w-px bg-black/10" aria-hidden />
                    <button
                      type="button"
                      onClick={() => movePost(p, "down")}
                      disabled={idx === posts.length - 1}
                      data-testid="admin-post-move-down"
                      title="Move down"
                      className="px-2 py-2 text-[var(--brand-body)] hover:bg-black/5 hover:text-[var(--brand-ink)] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                  <button
                    onClick={() => togglePublish(p)}
                    data-testid="admin-toggle-publish"
                    className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-2 label-kicker hover:border-[#0A0F17]"
                  >
                    {p.published ? <EyeOff size={14} /> : <Eye size={14} />}
                    {p.published ? "Unpublish" : "Publish"}
                  </button>
                  {p.published ? (
                    <button
                      onClick={() => sendPush(p)}
                      data-testid="admin-send-push"
                      disabled={pushBusy === p.id}
                      title="Send a push notification for this post to every subscribed device."
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#6CABDD] bg-[#6CABDD]/10 px-3 py-2 label-kicker text-[var(--brand-ink)] hover:border-[#0A0F17] hover:bg-[#6CABDD]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {pushBusy === p.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Send size={14} />
                      )}
                      Push
                    </button>
                  ) : null}
                  <button
                    onClick={() => toggleArchive(p)}
                    data-testid="admin-toggle-archive"
                    className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-2 label-kicker hover:border-[#0A0F17]"
                  >
                    {p.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                    {p.archived ? "Unarchive" : "Archive"}
                  </button>
                  <Link
                    to={`/admin/posts/${p.id}/edit`}
                    data-testid="admin-edit-post"
                    className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-2 label-kicker hover:border-[#0A0F17]"
                  >
                    <Edit3 size={14} /> Edit
                  </Link>
                  <button
                    onClick={() => copyPostUrl(p)}
                    data-testid="admin-copy-post-url"
                    title={`Copy post URL: /post/${p.slug || p.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 label-kicker transition-colors ${
                      copiedPostId === p.id
                        ? "border-green-600 bg-green-50 text-green-700"
                        : "border-black/10 hover:border-[#0A0F17]"
                    }`}
                  >
                    {copiedPostId === p.id ? (
                      <>
                        <Check size={14} /> Copied
                      </>
                    ) : (
                      <>
                        <Link2 size={14} /> Copy URL
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => duplicatePost(p)}
                    data-testid="admin-duplicate-post"
                    title="Clone this post as a new draft"
                    className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-2 label-kicker hover:border-[#0A0F17]"
                  >
                    <Copy size={14} /> Duplicate
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid="admin-delete-post"
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#E63946] px-3 py-2 label-kicker text-[#E63946] hover:bg-[#E63946] hover:text-white"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </li>
              );
            })}
          </ul>
        )}

        {!loading && filteredPosts.length > POSTS_PAGE_SIZE ? (
          <div
            className="mt-6 flex flex-wrap items-center justify-between gap-3"
            data-testid="admin-post-pagination"
          >
            <span className="label-kicker text-[var(--brand-body)]" data-testid="admin-post-pagination-range">
              Showing {pageStart + 1}–{Math.min(pageStart + POSTS_PAGE_SIZE, filteredPosts.length)} of {filteredPosts.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((cur) => Math.max(1, cur - 1))}
                disabled={page === 1}
                data-testid="admin-post-page-prev"
                className="rounded-md border border-black/10 bg-white px-3 py-2 label-kicker hover:border-[#0A0F17] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  data-testid={`admin-post-page-${n}`}
                  className={`h-9 w-9 rounded-md border font-display text-sm font-bold ${
                    n === page
                      ? "border-[#6CABDD] bg-[#6CABDD] text-white"
                      : "border-black/10 bg-white text-[var(--brand-ink)] hover:border-[#0A0F17]"
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((cur) => Math.min(totalPages, cur + 1))}
                disabled={page === totalPages}
                data-testid="admin-post-page-next"
                className="rounded-md border border-black/10 bg-white px-3 py-2 label-kicker hover:border-[#0A0F17] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}

// Compact category-button row (Admin, Media, Interactive, Design, Content,
// Shop) — clicking one expands its tools in a single panel below the row;
// clicking the open one again, or a different one, closes/switches it.
// Only ever one category open at a time (unlike the old per-group
// independent toggles), so this owns its own `activeId` state rather than
// each button owning its own open/closed flag.
function AdminNavCategories() {
  const [activeId, setActiveId] = useState(null);
  const activeGroup = NAV_GROUPS.find((g) => g.id === activeId) || null;

  return (
    <div className="mb-8">
      <div className="flex flex-wrap gap-2" data-testid="admin-nav-categories">
        {NAV_GROUPS.map((group) => {
          const active = group.id === activeId;
          return (
            <button
              key={group.id}
              type="button"
              onClick={() => setActiveId((cur) => (cur === group.id ? null : group.id))}
              aria-expanded={active}
              data-testid={`admin-nav-group-${group.id}-toggle`}
              className={`inline-flex items-center gap-1.5 rounded-md border px-4 py-2 font-display text-sm font-bold uppercase tracking-wide transition-colors ${
                active
                  ? "border-[#0A0F17] bg-[#0A0F17] text-white"
                  : "border-black/10 bg-white hover:border-[#0A0F17]"
              }`}
            >
              {group.label}
              <ChevronDown
                size={14}
                className={`shrink-0 transition-transform ${active ? "rotate-180" : ""}`}
              />
            </button>
          );
        })}
      </div>

      {activeGroup ? (
        <div
          className="mt-3 flex flex-wrap gap-2 rounded-md border border-black/10 bg-white p-4"
          data-testid={`admin-nav-group-${activeGroup.id}`}
        >
          {activeGroup.items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className="inline-flex items-center gap-2 rounded-md border border-black/10 bg-white px-4 py-2 font-display text-sm font-semibold hover:border-[#0A0F17]"
            >
              {item.label} →
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

